<template>
  <div
    v-if="product?.gemstones?.length"
    class="gemstone-drawer"
  >
    <button
      class="gemstone-drawer__trigger text-body-s-regular"
      type="button"
      @click="openDrawer"
      @mouseover="queryMetaobject()"
      @touchstart="queryMetaobject()"
      @focus="queryMetaobject()"
    >
      <span
        class="text-dark-grey"
        v-text="$string('product.gemstone_drawer.cta')"
      />
      <icon-arrow-right />
    </button>

    <teleport to="body">
      <overlay
        :key="namespace"
        type="drawer"
        :namespace="namespace"
        direction="left"
        block-class="gemstone-drawer-overlay"
        :source="analyticsSource"
      >
        <template #title>
          <h2
            class="text-other-label-medium"
            v-text="$string('product.gemstone_drawer.drawer_title')"
          />
        </template>

        <template #body="overlay">
          <tab-list
            v-slot="{activeIndex}"
            :overlay="overlay"
            :tabs="gemstones"
            :namespace="namespace"
            :source="analyticsSource"
            @active-tab="data => activeTab = data.tab"
          >
            <div class="gemstone-drawer-overlay__content">
              <tab-panel
                v-for="(gem, index) in gemstones"
                v-show="activeIndex === index"
                :key="gem.name"
                :namespace="namespace"
                :index="index"
              >
                <!-- RTE -->
                <div
                  v-if="gem.content"
                  class="rte"
                  v-html="gem.content"
                />

                <!-- Photos and videos -->
                <div
                  v-if="gem.media?.length"
                  class="gemstone-drawer-overlay__media-container"
                >
                  <div
                    v-for="(media, mediaIndex) in gem.media"
                    :key="(media.previewImage || media.image)?.id"
                    class="gemstone-drawer-overlay__media"
                    :class="{
                      'gemstone-drawer-overlay__media--portrait': isGemMediaPortrait(gem.media, mediaIndex)
                    }"
                  >
                    <responsive-image
                      v-if="media.image"
                      :image="media.image"
                      :padding="false"
                      :sizes="isGemMediaPortrait(gem.media, mediaIndex) ? '234px' : '480px'"
                      min-max="150-800"
                    />
                    <video-player
                      v-else
                      is-autoplay
                      :video="media"
                    />
                  </div>
                </div>
              </tab-panel>
            </div>
          </tab-list>

          <!-- Product recommendations -->
          <div
            v-if="activeTab?.products.length && isOverlayOpen"
            :key="activeTab"
            class="flex--col mt-xl mt-3xl-desktop"
          >
            <h3
              class="text-body-m-medium mb-m"
              v-text="$string('product.gemstone_drawer.discover_more')"
            />

            <product-slides
              :products="activeTab.products"
              source="gemstone_info"
              :options="splideOptions"
            />
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Gemstone drawer (gemstone-drawer)
 * -----------------------------------------------------------------------------
 * A drawer with tab lists that displays information about gemstone(s). It
 * displays text, images, and recommended products.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import ResponsiveImage from '~global/images/responsive-image'
import Overlay from '~global/overlay/overlay'

import ProductSlides from '~async/product-slides/product-slides'
import TabList from '~async/tab-list/tab-list'
import TabPanel from '~async/tab-list/tab-panel'
import VideoPlayer from '~async/video-player/video-player'

import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'

import { formatProduct } from '~/helpers/format'
import { transformMetaobjectFields } from '~/helpers/graphql'
import { convertSchemaToHtml } from '~/helpers/graphql-richtext'

import { values as breakpoints } from '~/config/breakpoints'

import { track } from '~/helpers/datalayer-actions'
import metaobjectQuery from '~graphql/gemstoneMetaobject.gql'

export default {
  name: 'GemstoneDrawer',

  components: {
    IconArrowRight,
    Overlay,
    TabList,
    TabPanel,
    ResponsiveImage,
    VideoPlayer,
    ProductSlides,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      namespace: 'gem-info',
      analyticsSource: 'gem_info',
      gemstones: [],
      queriedProduct: null,
      activeTab: null,
      isOverlayOpen: false,
      splideOptions: {
        gap: 'var(--spacing-s)',
        padding: 'var(--layout-margin)',
        perPage: 1.45,
        arrows: false,
        keyboard: true,
        wheel: false,
        breakpoints: {
          [breakpoints.l]: {
            perPage: 2,
            padding: 0,
            pagination: false,
            arrows: true,
          },
        },
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeOverlay: 'overlays/getActiveOverlay',
    }),
  },

  watch: {

    /**
     * Watch the active overlay to update the isOverlayOpen variable with a delay.
     * Splide has problems rendering if its parent container is hidden so we use this to
     * delay rendering the carousel until the drawer is open.
     * @param overlay - The active overlay
     */
    async activeOverlay(overlay) {
      if (this.namespace !== overlay?.namespace) {
        return
      }
      await cnvs.Sleep(this.$timing('normal'))
      this.isOverlayOpen = true
    },
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Open the gemstone drawer
     */
    openDrawer() {
      this.openOverlay({
        namespace: this.namespace,
        ignoreDismissed: true,
      })
      track('gem_info_click')
    },

    /**
     * Get gemstone info metaobject
     */
    async queryMetaobject() {
      if (!this.product?.gemstones?.length || this.product?.handle === this.queriedProduct) {
        return
      }
      try {
        // Query all the metaobjects by handle
        const promises = this.product.gemstones.map((id) => cnvs.Query({
          query: metaobjectQuery,
          variables: { id },
        }))
        const response = (await Promise.all(promises)).filter(Boolean)

        // Format metaobjects and products from the response
        const gemstones = []
        response.forEach(({ metaobject }) => {
          const { gemstone, products } = transformMetaobjectFields(metaobject.fields)
          const formattedGemstone = transformMetaobjectFields(gemstone.fields)
          const formattedProducts = products?.map((product) => formatProduct({ product })) || []

          if (!formattedGemstone?.name) {
            return
          }

          gemstones.push({
            ...formattedGemstone,
            products: formattedProducts,
            content: convertSchemaToHtml(formattedGemstone.content),
            label: formattedGemstone.name,
          })
        })

        this.gemstones = gemstones
        this.queriedProduct = this.product.handle
        this.activeTab = gemstones[0]
      } catch (error) {
        console.error('Issue with fetching gemstone information', error)
      }
    },

    /**
     * Determine if the gemstone image/video should display as portrait
     * @param {array} media - The media array in gem metaobject
     * @param index - The media index in its array
     */
    isGemMediaPortrait(media, index) {
      return media.length % 2 === 0 || (media.length === 3 && index > 0)
    },
  },
}
</script>

<style lang="scss">
@import './gemstone-drawer';
</style>
